// Pure helpers for the navigator quality-check screen (ScanQualityPage). Kept apart from the component so the rules that decide what a
// navigator may choose, and what is sent, are testable without rendering.

export const KIND_LABEL = {
  VALUE_MISMATCH: 'The two readings give different numbers',
  ONLY_IN_OCR: 'Only the text reader found this row',
  ONLY_IN_CLAUDE: 'Only the second reader found this row',
};

// The choices offered for each kind of difference, in the order shown. `needsValue` / `needsNote` mirror the server's validation
// (ScanQualityService.validate) so the screen never offers a submit the server would refuse.
export const CHOICES = {
  VALUE_MISMATCH: [
    { action: 'ACCEPT_OCR', label: 'Use the text reader’s number' },
    { action: 'ACCEPT_CLAUDE', label: 'Use the second reader’s number' },
    { action: 'MANUAL', label: 'Enter the number from the report', needsValue: true, needsNote: true },
  ],
  ONLY_IN_OCR: [
    { action: 'ACCEPT_OCR', label: 'Yes, this row is on the report' },
    { action: 'MANUAL', label: 'It is on the report, but the number is wrong', needsValue: true, needsNote: true },
    { action: 'DISCARD', label: 'No, this is not a result row', needsNote: true },
  ],
  ONLY_IN_CLAUDE: [
    { action: 'ACCEPT_CLAUDE', label: 'Yes, add this row' },
    { action: 'DISCARD', label: 'No, ignore it', needsNote: true },
  ],
};

const PLAIN_NUMBER = /^[<>]?\d{1,9}(?:\.\d{1,6})?$/;

export const choiceFor = (kind, action) => (CHOICES[kind] || []).find((c) => c.action === action);

/** The problem with one pending decision, or null when it can be sent. */
export function decisionError(kind, decision) {
  if (!decision || !decision.action) return null;           // nothing chosen yet: not an error, just not sent
  const choice = choiceFor(kind, decision.action);
  if (!choice) return 'That choice does not apply here.';
  if (choice.needsValue && !PLAIN_NUMBER.test(String(decision.value ?? '').trim())) return 'Enter the number as digits, for example 13.5';
  if (choice.needsNote && !String(decision.note ?? '').trim()) return 'Add a short note saying why.';
  return null;
}

/** The request body for the decisions made so far; undecided or still-invalid items are left out. */
export function buildResolutions(differences, decisions) {
  return differences
    .filter((d) => d.status === 'OPEN' && decisions[d.id]?.action && !decisionError(d.kind, decisions[d.id]))
    .map((d) => {
      const x = decisions[d.id];
      return {
        discrepancyId: d.id,
        action: x.action,
        ...(x.action === 'MANUAL' ? { value: String(x.value).trim() } : {}),
        ...(x.note && String(x.note).trim() ? { note: String(x.note).trim() } : {}),
      };
    });
}

export const openCount = (differences) => differences.filter((d) => d.status === 'OPEN').length;

export const ACTION_LABEL = {
  ACCEPT_OCR: 'Used the text reader’s reading',
  ACCEPT_CLAUDE: 'Used the second reader’s reading',
  MANUAL: 'Entered from the report',
  DISCARD: 'Not a result row',
};

/**
 * A trend report (one result column per draw date) is split into one record per column, and each column's date becomes that record's
 * draw date. So every column needs exactly one real, past, distinct date before the report can be released. `dates` maps column index
 * to a yyyy-MM-dd string (what the navigator has typed or the server pre-filled). Mirrors ScanQualityService.validatedColumns.
 */
export function columnDatesState(columns, dates, today = new Date()) {
  if (!Array.isArray(columns) || columns.length < 2) return { needed: false, valid: true, payload: [], error: null };
  const limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).getTime();
  const seen = new Set();
  const payload = [];
  for (const c of columns) {
    const v = String(dates?.[c.index] ?? '').trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
    const real = d && d.getFullYear() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]);
    if (!real || d.getFullYear() < 2000 || d.getTime() > limit) {
      return { needed: true, valid: false, payload: [], error: 'Enter a real draw date (not in the future) for every column.' };
    }
    if (seen.has(v)) return { needed: true, valid: false, payload: [], error: 'Two columns cannot have the same date.' };
    seen.add(v);
    payload.push({ index: c.index, date: v });
  }
  return { needed: true, valid: true, payload, error: null };
}

/** "13 Oct 2026" for a yyyy-MM-dd date; the date as printed on the report when there is none; "Column 2" as a last resort. */
export function columnLabel(columns, col) {
  const c = (columns || []).find((x) => x.index === col);
  if (!c) return '';
  if (c.date) {
    const [y, m, d] = c.date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return c.label || `Column ${col + 1}`;
}

/**
 * What the Save button may do. A report is released only when every difference is settled AND the agreed rows are confirmed (AND, for a
 * trend report, every column's date is confirmed), so the button is live when there is at least one valid decision to send, or the
 * agreed rows or the dates are being confirmed now. Any half-filled decision or invalid date blocks it: nothing is sent that the
 * navigator has not finished.
 */
export function saveState(differences, decisions, {
  agreedConfirmed, attest, columns = [], columnsConfirmed = true, columnDates = {}, attestDates = false, undatedPages = [], undatedColumn = null,
  textPages = [], pendingAdds = [], removedAddedIds = [],
}) {
  const resolutions = buildResolutions(differences, decisions);
  const invalidDecision = differences.some((d) => d.status === 'OPEN' && decisions[d.id]?.action && decisionError(d.kind, decisions[d.id]));
  const confirmNow = !agreedConfirmed && !!attest;
  const dates = columnDatesState(columns, columnDates);
  const confirmDatesNow = dates.needed && !columnsConfirmed && !!attestDates;
  // pages with no dates of their own, and pages with text but no results, must be placed in one draw
  const placedPages = placedPagesOf(undatedPages, textPages);
  const undatedOk = placedPages.length === 0 || (Number.isInteger(undatedColumn) && columns.some((c) => c.index === undatedColumn));
  const invalidDates = confirmDatesNow && (!dates.valid || !undatedOk);
  const invalid = invalidDecision || invalidDates;
  const addRows = pendingAdds.map(toNewRow);
  const changesAdds = addRows.length > 0 || removedAddedIds.length > 0;
  return {
    resolutions, confirmNow, invalid, dates, confirmDatesNow,
    columnDates: confirmDatesNow && dates.valid && undatedOk ? dates.payload : null,
    undatedColumn: confirmDatesNow && placedPages.length > 0 ? undatedColumn : null,
    undatedOk,
    addRows: addRows.length > 0 ? addRows : null,
    removeAddedRows: removedAddedIds.length > 0 ? removedAddedIds : null,
    canSave: !invalid && (resolutions.length > 0 || confirmNow || confirmDatesNow || changesAdds),
  };
}

/** Distinct page numbers, ascending, of the pages that must be placed in a draw (no dates of their own, or text with no results). */
export function placedPagesOf(undatedPages = [], textPages = []) {
  return [...new Set([...(undatedPages || []), ...(textPages || [])])].sort((a, b) => a - b);
}

/** What a navigator can confirm for a culture page. Mirrors ScanQualityService.CULTURE_RESULTS (the registry matches this exact text). */
export const CULTURE_RESULTS = ['No growth', 'No growth (preliminary)', 'Growth', 'Mixed growth'];

/** A culture is reported in words, not as a number. */
export const isCultureName = (name) => /culture/i.test(String(name ?? ''));

/**
 * The summary lines of a suggested text page, as rows to lay out: a section heading ("Microbiology"), the test name, "Label : value"
 * pairs, an indented label / value pair for a result line (two or more spaces between them), a status word ("Preliminary"), or
 * plain text. The words are the scan's; only their presentation is decided here.
 */
export function summaryRows(lines = []) {
  const rows = [];
  (lines || []).forEach((raw, i) => {
    const line = String(raw ?? '').trim();
    if (!line) return;
    const pair = line.match(/^([^:]{1,24}?)\s*:\s*(.+)$/);
    const gap = line.split(/\s{2,}/);
    if (/^(preliminary|final|provisional)$/i.test(line)) rows.push({ kind: 'status', text: line });
    else if (/culture|^procalcitonin$/i.test(line) && !pair && gap.length === 1) rows.push({ kind: 'name', text: line });
    else if (i === 0 && gap.length === 1 && !pair && line.length <= 30) rows.push({ kind: 'section', text: line });
    else if (pair) rows.push({ kind: 'pair', label: pair[1].trim(), value: pair[2].trim() });
    else if (gap.length >= 2) rows.push({ kind: 'result', label: gap[0], value: gap.slice(1).join('  ') });
    else rows.push({ kind: 'text', text: line });
  });
  return rows;
}

/** A numeric result ready to add from what a page suggests (procalcitonin), after the navigator confirms the value. */
export function numericDraft(page, name, value, unit) {
  return { ...EMPTY_DRAFT, page: String(page), name, value: String(value), unit: unit || '', note: `Confirmed from page ${page} of the original` };
}

/** A culture result ready to add, from what the page suggests (the navigator confirms it first). */
export function cultureDraft(page, name, result) {
  return { ...EMPTY_DRAFT, page: String(page), name, value: result, note: `Confirmed from page ${page} of the original` };
}

export const EMPTY_DRAFT = { page: '', col: '0', name: '', value: '', unit: '', lo: '', hi: '', note: '' };

const asNumber = (v) => (String(v ?? '').trim() === '' ? null : Number(String(v).trim()));

/** The problem with a result being added, or null when it can be added. Mirrors ScanQualityService.validatedAdded. */
export function newRowError(d, pageCount, columns = []) {
  const page = Number(d.page);
  if (!Number.isInteger(page) || page < 1 || page > pageCount) return `Choose a page between 1 and ${pageCount}.`;
  const col = Number(d.col);
  const cols = Math.max(1, columns.length);
  if (!Number.isInteger(col) || col < 0 || col >= cols) return 'Choose which draw this result belongs to.';
  const name = String(d.name ?? '').trim();
  if (!name || name.length > 100) return 'Enter the marker name (up to 100 characters).';
  if (isCultureName(name)) {
    if (!CULTURE_RESULTS.some((c) => c.toLowerCase() === String(d.value ?? '').trim().toLowerCase())) return `Choose the culture result: ${CULTURE_RESULTS.join(', ')}.`;
    if (String(d.unit ?? '').trim() || String(d.lo ?? '').trim() || String(d.hi ?? '').trim()) return 'A culture result has no unit or reference range.';
    return String(d.note ?? '').trim() ? null : 'Add a short note saying where you read it.';
  }
  if (!PLAIN_NUMBER.test(String(d.value ?? '').trim())) return 'Enter the value as digits, for example 38.';
  if (String(d.unit ?? '').trim().length > 20) return 'The unit is too long.';
  const lo = asNumber(d.lo);
  const hi = asNumber(d.hi);
  if ((lo === null) !== (hi === null) || Number.isNaN(lo) || Number.isNaN(hi)) return 'Enter both ends of the reference range as numbers, or neither.';
  if (lo !== null && lo > hi) return 'The reference range is the wrong way round (low first, then high).';
  if (!String(d.note ?? '').trim()) return 'Add a short note saying where you read it.';
  return null;
}

/** The request body for one result being added. */
export function toNewRow(d) {
  return {
    page: Number(d.page), col: Number(d.col), name: String(d.name).trim(), value: String(d.value).trim(),
    unit: String(d.unit ?? '').trim(), lo: asNumber(d.lo), hi: asNumber(d.hi), note: String(d.note).trim(),
  };
}

/** "page 2" / "pages 2 and 3" / "pages 2, 3 and 5" for the pages that were not cross-checked automatically; '' when there are none. */
export function uncheckedLabel(pages) {
  const n = (Array.isArray(pages) ? pages : []).map((p) => p.page).sort((a, b) => a - b);
  if (n.length === 0) return '';
  if (n.length === 1) return `page ${n[0]}`;
  return `pages ${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

/** The column with the most recent valid date: the natural home of pages that show no dates (a default the navigator confirms). */
export function latestColumnIndex(columns, dates) {
  let best = null;
  let bestDate = '';
  for (const c of columns || []) {
    const v = String(dates?.[c.index] ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(v) && v > bestDate) { bestDate = v; best = c.index; }
  }
  return best;
}

/** The grid row (one marker across every draw date) that holds the cell for this difference, or null. */
export function gridRowFor(grid, differenceId) {
  return (grid || []).find((r) => (r.cells || []).some((c) => c.differenceId === differenceId)) || null;
}

/** What to show in a grid cell: the value(s) and how to treat them. `settled` wins once a navigator has decided. */
export function cellView(cell) {
  if (!cell) return { kind: 'none', text: '—', ai: '—' };
  if (cell.state === 'AGREED') return { kind: 'agreed', text: cell.text, ai: cell.ai };
  const base = { text: cell.text ?? '—', ai: cell.ai ?? '—' };
  if (cell.settled != null && cell.differenceId) return { ...base, kind: 'settled', settled: cell.settled };
  return { ...base, kind: 'open' };
}

/** The columns with the dates the navigator has typed so far shown in place of the ones the readers found (so headers follow the inputs). */
export function withTypedDates(columns, dates, confirmed) {
  return (columns || []).map((c) => (confirmed ? c : { ...c, date: (dates && dates[c.index]) || c.date }));
}

/** What the upload's processing status means for someone looking at an approved check: has the report been produced yet? */
export function approvedStatusLabel(status) {
  switch (status) {
    case 'TRIAGED': return { text: 'Triaged', tone: 'ok' };
    case 'FAILED': return { text: 'Triage failed', tone: 'bad' };
    case 'NEEDS_MANUAL_REVIEW': return { text: 'Needs review', tone: 'bad' };
    case 'AWAITING_IDENTITY_CONFIRMATION': return { text: 'Needs identity confirmation', tone: 'bad' };
    case 'EXTRACTING':
    case 'EXTRACTED': return { text: 'Triage pending', tone: 'wait' };
    default: return { text: status || '—', tone: 'wait' };
  }
}
